// 左側主選單（SPEC #vms-web-220「資訊架構與導覽」，VMS UX 評審 2026-10-13 P0-4）：
//   - 照工作對象與角色分三組：監看（每個人）／設備與場域／組織管理；組內沒有可見項目時整組（含標題）不畫
//   - 沒有權限的整個不出現（不是灰掉）；選單名 = 頁標題（<h1>）= 瀏覽器分頁標題
//   - 個人設定（我的帳號，/settings）不在選單，在頁首使用者選單
// 資料：GET /v1/me/devices（每台的 actions、家戶角色）與 GET /v1/me/orgs（組織角色、org_permissions、node_enroll），lib/scope.tsx。

export type NavGroup = 'watch' | 'it' | 'manage' | 'org';

export const NAV_GROUPS: { key: NavGroup; labelKey: string }[] = [
  { key: 'watch', labelKey: 'navg.watch' },
  // Kinio KMS：開了 KMS 的才有，排在「監看」與「設備與場域」之間（SPEC #vms-web-220、#kinio-kms-ui 導覽）
  { key: 'it', labelKey: 'kmgr:nav.group' },
  { key: 'manage', labelKey: 'navg.manage' },
  { key: 'org', labelKey: 'navg.org' },
];

export interface NavItem {
  key: string;
  group: NavGroup;
  path: string;
  /** 誰看得到（visibleNav 的條件，對應 SPEC 表格的「誰看得到」欄）。 */
  needs:
    | 'signed_in'
    | 'live'
    | 'playback'
    | 'events'
    | 'map'
    | 'exports'
    | 'devices'
    | 'sites'
    | 'rules'
    | 'ai'
    | 'backup'
    | 'people'
    | 'audit'
    | 'billing'
    | 'org'
    | 'kmgr'
    | 'siteroom'
    | 'energy';
  /** 選單文字的 i18n key（沒給 = nav.<key>）；dev 專用的畫面用 dev 命名空間。 */
  labelKey?: string;
  /** 同義詞（全域搜尋的「頁面」比對用，i18n key；值是逗號分隔）。 */
  synonymsKey?: string;
}

export const NAV: NavItem[] = [
  { key: 'overview', group: 'watch', path: '/', needs: 'signed_in' },
  { key: 'live', group: 'watch', path: '/live', needs: 'live' },
  { key: 'playback', group: 'watch', path: '/playback', needs: 'playback' },
  { key: 'events', group: 'watch', path: '/events', needs: 'events' },
  { key: 'map', group: 'watch', path: '/map', needs: 'map' },
  { key: 'exports', group: 'watch', path: '/exports', needs: 'exports' },
  // IT 監控（Kinio KMS：事故 M1-14、主機與網路與應用 M2-13、遠端協助 M4-06）
  { key: 'kmgr-incidents', group: 'it', path: '/kmgr/incidents', needs: 'kmgr', labelKey: 'kmgr:nav.incidents' },
  { key: 'kmgr-hosts', group: 'it', path: '/kmgr/hosts', needs: 'kmgr', labelKey: 'kmgr:nav.hosts' },
  { key: 'kmgr-network', group: 'it', path: '/kmgr/network', needs: 'kmgr', labelKey: 'kmgr:nav.network' },
  { key: 'kmgr-remote', group: 'it', path: '/kmgr/remote', needs: 'kmgr', labelKey: 'kmgr:nav.remote' },
  { key: 'devices', group: 'manage', path: '/devices', needs: 'devices' },
  // 用電（plan SPEC NRG-V1）：對任一家戶有 energy.read 而且那個家戶有量測點時出現（不看目前範圍；features/energy/hooks.ts useEnergyNav）
  { key: 'energy', group: 'manage', path: '/energy', needs: 'energy', labelKey: 'energy:title' },
  { key: 'sites', group: 'manage', path: '/sites', needs: 'sites' },
  { key: 'rules', group: 'manage', path: '/rules', needs: 'rules' },
  { key: 'ai', group: 'manage', path: '/ai', needs: 'ai' },
  { key: 'backup', group: 'manage', path: '/backup', needs: 'backup' },
  // 機房：家戶 owner / admin 經 station.use 查機房管理站（網路、伺服器、NAS；只有查詢，plan SPEC #kinio-station-seat-site-share）
  { key: 'siteroom', group: 'manage', path: '/siteroom', needs: 'siteroom', labelKey: 'siteroom:nav' },
  // Kinio Station 虛擬工位（只讀；dev plane + kinio-family 品牌 + 對 Station 有 device.read + 讀得到工位資料，SPEC #kinio-station-seat-vms）
  { key: 'people', group: 'org', path: '/people', needs: 'people' },
  { key: 'audit', group: 'org', path: '/audit', needs: 'audit' },
  { key: 'billing', group: 'org', path: '/billing', needs: 'billing' },
  { key: 'org', group: 'org', path: '/org', needs: 'org' },
];

export interface NavContext {
  /** 「我的設備」裡至少一台給了的動作（含通道）。 */
  actions: Set<string>;
  /** 任一家戶的家戶角色是 owner / admin。 */
  homeAdmin: boolean;
  /** 任一家戶的家戶角色是 owner / admin / member（規則頁）。 */
  homeMember: boolean;
  /** 某個 team 組織的 owner / org_admin。 */
  teamAdmin: boolean;
  /** 某個 team 組織有這些組織層權限（owner / org_admin 隱含全部）。 */
  teamPerms: Set<string>;
  /** 某個 team 組織的 node_enroll 是 true（owner / org_admin / 有效 node_admin，SPEC #provisioning-s3-8-org）。 */
  nodeEnroll: boolean;
  /** 個人組織裡有家戶、自己是擁有者（個人組織也能建節點、有組織設定）。 */
  personalOwner: boolean;
  /** 個人組織家戶的成員（任何家戶角色；方案頁看得到、只有 owner 能買）。 */
  personalHome: boolean;
  /** 有任一家戶 kmgr_enabled 且對它有 kmgr.read（features/kmgr/hooks.ts useKmgrNav）。 */
  kmgr?: boolean;
  /** 至少一台機房管理站有可分享工具（features/siteroom/useSiteRoom.ts useSiteRoomNav）。 */
  siteroom?: boolean;
  /** 對任一家戶有 energy.read 而且那個家戶有量測點（features/energy/hooks.ts useEnergyNav）。 */
  energy?: boolean;
}

export function navVisible(it: NavItem, c: NavContext): boolean {
  switch (it.needs) {
    case 'signed_in':
      return true;
    case 'live':
      return c.actions.has('media.live');
    case 'playback':
      return c.actions.has('recording.read');
    case 'events':
      return c.actions.has('event.read');
    case 'map':
      return c.actions.has('device.read');
    case 'exports':
      return c.actions.has('recording.export');
    case 'devices': // 任一台 device.read；或組織 owner / org_admin / org.devices；或 node_enroll（沒有任何家戶也出現）
      return c.actions.has('device.read') || c.teamPerms.has('org.devices') || c.nodeEnroll;
    case 'sites': // team 組織 owner / org_admin / org.sites，或節點管理員，或個人組織裡有家戶的擁有者；只有成員身分的保全不出現
      return c.teamPerms.has('org.sites') || c.nodeEnroll || c.personalOwner;
    case 'rules':
      return c.homeMember;
    case 'ai':
      return c.homeAdmin || c.teamAdmin;
    case 'backup':
      return c.homeAdmin;
    case 'people':
      return c.teamAdmin || c.homeAdmin;
    case 'audit':
      return c.teamPerms.has('org.audit') || c.personalOwner;
    case 'billing': // 買 / 指派的權限在頁內與伺服器端；保全（只是 team 組織成員）不出現（SPEC 驗收：只有監看 6 項與「設備」）
      return c.teamAdmin || c.teamPerms.has('org.billing') || c.personalHome;
    case 'org':
      return c.teamAdmin || c.personalOwner;
    case 'kmgr':
      return !!c.kmgr;
    case 'siteroom':
      return !!c.siteroom;
    case 'energy':
      return !!c.energy;
  }
}

export function visibleNav(ctx: NavContext, items: NavItem[] = NAV): NavItem[] {
  return items.filter((it) => navVisible(it, ctx));
}

/** 三組依序，組內沒有可見項目的不列。 */
export function groupedNav(items: NavItem[]): { group: NavGroup; labelKey: string; items: NavItem[] }[] {
  return NAV_GROUPS.map((g) => ({ group: g.key, labelKey: g.labelKey, items: items.filter((i) => i.group === g.key) })).filter((g) => g.items.length > 0);
}

/**
 * 預設展開：監看永遠展開；設備與場域在有任一設備或場域管理權時（保全只有唯讀的「設備」也要看得到，SPEC：監看 6 項加「設備」，
 * 所以看得到任一台設備也算）；組織管理在 team 組織 owner / org_admin 時（之後記住使用者的選擇）。
 */
export function defaultExpanded(g: NavGroup, c: Pick<NavContext, 'teamAdmin' | 'homeAdmin' | 'teamPerms' | 'nodeEnroll' | 'actions'>): boolean {
  if (g === 'watch' || g === 'it') return true;
  if (g === 'manage') return c.actions.has('device.read') || c.homeAdmin || c.teamAdmin || c.teamPerms.has('org.devices') || c.teamPerms.has('org.sites') || c.nodeEnroll;
  return c.teamAdmin;
}

const COLLAPSE_KEY = 'vms.nav.collapsed';

/** 使用者收合 / 展開過的組（true = 收合、false = 展開）；讀不到時用預設。 */
export function savedNavState(): Partial<Record<NavGroup, boolean>> {
  try {
    const v = JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}') as Record<string, unknown>;
    const out: Partial<Record<NavGroup, boolean>> = {};
    for (const g of ['it', 'manage', 'org'] as const) if (typeof v[g] === 'boolean') out[g] = v[g] as boolean;
    return out;
  } catch {
    return {};
  }
}

export function saveNavState(s: Partial<Record<NavGroup, boolean>>): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(s));
  } catch {
    /* 無 storage */
  }
}

/** 目前網址對到哪個選單項（頁標題用）；/ 只對 /，其他取最長前綴。 */
export function navItemOf(pathname: string, items: NavItem[] = NAV): NavItem | undefined {
  if (pathname === '/') return items.find((i) => i.path === '/');
  return items
    .filter((i) => i.path !== '/' && (pathname === i.path || pathname.startsWith(i.path + '/')))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/** 人員與存取舊分頁 → 存取權的來源篩選（SPEC #vms-web-220 轉址表）。 */
const PEOPLE_SOURCE: Record<string, string> = { grants: 'site_grant', homes: 'home_role', shares: 'share' };

/**
 * 舊網址轉址（SPEC #vms-web-220 轉址表；書籤、通知、email 裡的連結照用）。回 null = 不用轉。
 * /people/grants|homes|shares → /people/access?source=…；/people/delegations[/…]、/people/org-shares → /people/external[/…]；
 * /verify 在殼層裡（登入後從選單進）→ /exports/verify（免登入的 /verify 給外部收件人，在 App.tsx 殼層外處理）。
 */
/** 一級頁的分頁路徑（`?tab=<值>` 轉成 `/<頁>/<值>`）；all = 第一個分頁。 */
const TAB_PATHS: Record<string, string[]> = {
  settings: ['notifications', 'shared', 'local-vms', 'developer-account'],
  devices: ['all', 'firmware', 'inventory'],
  people: ['access', 'members', 'roles', 'external', 'member-groups'],
};

export function legacyRedirect(pathname: string, search = ''): string | null {
  const m = (from: string, to: string): string | null => {
    if (pathname === from || pathname.startsWith(from + '/')) return to + pathname.slice(from.length) + search;
    return null;
  };
  const pm = /^\/people\/(grants|homes|shares)\/?$/.exec(pathname);
  if (pm) {
    const q = new URLSearchParams(search);
    q.set('source', PEOPLE_SOURCE[pm[1]]);
    return '/people/access?' + q.toString();
  }
  if (pathname === '/people/org-shares') return '/people/external' + search;
  // `?tab=` 形式的深連結跟路徑形式一樣有效（第四輪 B4-13）：/settings?tab=notifications → /settings/notifications
  const tm = /^\/(settings|devices|people)\/?$/.exec(pathname);
  if (tm) {
    const q = new URLSearchParams(search);
    const tab = q.get('tab') ?? '';
    if (TAB_PATHS[tm[1]]?.includes(tab)) {
      q.delete('tab');
      const rest = q.toString();
      return `/${tm[1]}${tab === 'all' ? '' : '/' + tab}${rest ? '?' + rest : ''}`;
    }
  }
  return (
    m('/inventory', '/devices/inventory') ??
    m('/settings/structure', '/sites') ??
    m('/settings/org', '/org') ??
    m('/people/delegations', '/people/external') ??
    (pathname === '/verify' ? '/exports/verify' + search : null)
  );
}
